/**
 * Bulk zip import (TZ-04 §D, Phase 2.3): upload a .zip of HTML emails,
 * the API queues one job that converts every entry, and this panel polls
 * GET /v1/migrations/{id} until it settles — showing live progress and a
 * succeeded/failed summary. Unlike single-HTML import there is no one
 * template to open, so the terminal CTA is "View templates".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../lib/api";
import {
  getMigration,
  importBulk,
  MAX_ZIP_BYTES,
  type MigrationJob,
  type SourcePlatform,
} from "../lib/migrations";
import { Button } from "./ui/button";

const PLATFORM_OPTIONS: Array<{ value: SourcePlatform; label: string }> = [
  { value: "generic", label: "Other / generic HTML" },
  { value: "sendpulse", label: "SendPulse" },
  { value: "stripo", label: "Stripo" },
  { value: "beefree", label: "Beefree" },
  { value: "mailchimp", label: "Mailchimp" },
];

function isTerminal(status: string | undefined): boolean {
  return status === "completed" || status === "failed" || status === "cancelled";
}

export function BulkImportPanel({
  onViewTemplates,
  onCancel,
}: {
  onViewTemplates: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [platform, setPlatform] = useState<SourcePlatform>("generic");
  const [rehostImages, setRehostImages] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: () =>
      importBulk({ file: file as File, sourcePlatform: platform, rehostImages }),
    onSuccess: (job) => {
      // Seed the cache so the queued state shows before the first poll.
      queryClient.setQueryData(["migration", job.id], job);
      setJobId(job.id);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : t("bulk_import.upload_failed"),
      );
    },
  });

  // Poll the job until it settles; the seeded queued job renders immediately.
  const { data: job } = useQuery({
    queryKey: ["migration", jobId],
    queryFn: () => getMigration(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (query) => (isTerminal(query.state.data?.status) ? false : 1200),
  });

  function onFilePicked(e: ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0];
    if (picked === undefined) return;
    setError(null);
    if (picked.size > MAX_ZIP_BYTES) {
      setError(t("bulk_import.too_large"));
      return;
    }
    setFile(picked);
  }

  function onSubmit(): void {
    setError(null);
    if (file === null) {
      setError(t("bulk_import.pick_zip_first"));
      return;
    }
    upload.mutate();
  }

  function reset(): void {
    setJobId(null);
    setFile(null);
    setError(null);
    upload.reset();
  }

  const done = isTerminal(job?.status);

  return (
    <section className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">
            {t("bulk_import.title")}
          </h2>
          <p className="text-sm text-neutral-600">{t("bulk_import.description")}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t("bulk_import.cancel")}
        </Button>
      </header>

      {jobId === null ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-neutral-700">
                {t("bulk_import.source_label")}
              </span>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as SourcePlatform)}
                className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
              >
                {PLATFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("bulk_import.choose_zip")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,application/zip"
                onChange={onFilePicked}
                className="hidden"
                aria-label={t("bulk_import.zip_file_aria")}
              />
              {file !== null && (
                <span className="text-xs text-neutral-500">{file.name}</span>
              )}
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={rehostImages}
              onChange={(e) => setRehostImages(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300"
            />
            <span>
              {t("import_html.rehost_images_label")}
              <span className="block text-xs text-neutral-500">
                {t("import_html.rehost_images_hint")}
              </span>
            </span>
          </label>

          {error !== null && (
            <p
              role="alert"
              className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
            >
              {error}
            </p>
          )}

          <Button
            type="button"
            variant="primary"
            loading={upload.isPending}
            onClick={onSubmit}
          >
            {t("bulk_import.submit")}
          </Button>
        </div>
      ) : (
        <BulkProgress
          job={job}
          done={done}
          onViewTemplates={onViewTemplates}
          onReset={reset}
        />
      )}
    </section>
  );
}

function BulkProgress({
  job,
  done,
  onViewTemplates,
  onReset,
}: {
  job: MigrationJob | undefined;
  done: boolean;
  onViewTemplates: () => void;
  onReset: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const total = job?.total_files ?? 0;
  const processed = job?.processed_files ?? 0;
  const succeeded = job?.succeeded_files ?? 0;
  const failed = job?.failed_files ?? 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;

  if (!done) {
    return (
      <div className="space-y-3" role="status" aria-live="polite">
        <p className="text-sm text-neutral-700">
          {total > 0
            ? t("bulk_import.processing", { processed, total })
            : t("bulk_import.queued")}
        </p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
          <div
            className="h-full rounded-full bg-primary-600 transition-all"
            style={{ width: `${total > 0 ? pct : 8}%` }}
          />
        </div>
      </div>
    );
  }

  const failedAll = job?.status === "failed";
  const noHtml = failedAll && total === 0;

  return (
    <div className="space-y-4" role="status" aria-live="polite">
      <div>
        <h3 className="text-base font-semibold text-neutral-900">
          {noHtml
            ? t("bulk_import.no_html")
            : failedAll
              ? t("bulk_import.all_failed")
              : t("bulk_import.done_title")}
        </h3>
        {!noHtml && (
          <p className="mt-1 text-sm text-neutral-600">
            {t("bulk_import.summary", { succeeded, failed })}
            {(job?.warnings_count ?? 0) > 0 && (
              <>
                {" · "}
                {t("bulk_import.warnings_count", { count: job?.warnings_count ?? 0 })}
              </>
            )}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        {succeeded > 0 && (
          <Button type="button" variant="primary" onClick={onViewTemplates}>
            {t("bulk_import.view_templates")}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onReset}>
          {t("bulk_import.import_another")}
        </Button>
      </div>
    </div>
  );
}
